import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import type { MasterCertificate } from '../hooks/useMasterCertificates';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ToggleButton } from 'primereact/togglebutton';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { findUnmappedPolicies, describePolicyLob } from '../utils/unmappedPolicies';
// import { Checkbox } from 'primereact/checkbox';
import { useSelectedClient } from '../../../hooks/useSelectedClient';
import { createMasterCertificate, editMasterCertificate, archiveMasterCertificate, ACORD_FORM_NUMBERS, fetchBuildingCoverages, insertCarrier } from '../api/masterCertificateApi';
import { applyInsurerSlots, ensureCarrierIds, type CarrierSlotDeps } from '../utils/carrierSlots';
import { resolveCarrierById } from './InsurerSearchDropdown';
import { Dialog } from 'primereact/dialog';
import PreviewSendCertificate from '../../send-certificate/components/PreviewSendCertificate';
import type { CoverageEntry } from '../api/masterCertificateApi';
import { buildAcord25Coverages } from '../utils/acord25Payload';
import { buildAcord24Coverages } from '../utils/acord24Payload';
import { buildAcord27Coverages } from '../utils/acord27Payload';
import { buildAcord28Coverages } from '../utils/acord28Payload';
import type { Acord25FormRef } from './acord25/Acord25Form';
import type { Acord24FormRef } from './acord24/Acord24Form';
import type { Acord27FormRef } from './acord27/Acord27Form';
import type { Acord28FormRef } from './acord28/Acord28Form';
import AddPolicyForm from '../../policies/AddPolicyForm';
import { emitPolicyCreated } from '../../../utilities/policyEvents';

import '../../../styles/pages.css';
import '../../../styles/panels.css';
import '../../../styles/mastercertificate.css';

import Acord25Form from './acord25/Acord25Form';
import Acord25With101Form from './acord25/Acord25With101Form';
import Acord24Form from './acord24/Acord24Form';
import Acord24With101Form from './acord24/Acord24With101Form';
import Acord27Form from './acord27/Acord27Form';
import Acord27With101Form from './acord27/Acord27With101Form';
import Acord28Form from './acord28/Acord28Form';
import Acord28With101Form from './acord28/Acord28With101Form';
import Acord30Form from './acord30/Acord30Form';
import Acord22Form from './acord22/Acord22Form';
import Acord20Form from './acord20/Acord20Form';
import Acord21Form from './acord21/Acord21Form';
import Acord23Form from './acord23/Acord23Form';
import Acord29Form from './acord29/Acord29Form';
import Acord75Form from './acord75/Acord75Form';
import Csio0910eclForm from './csio0910ecl/Csio0910eclForm';

/*
 * ACORD Form Registry
 * ───────────────────
 * To add a new ACORD type (e.g. ACORD 26, 27, 28, 75, 90):
 *   1. Create the form component (e.g. Acord26Form.tsx) in this folder
 *   2. Import it above
 *   3. Add an entry to ACORD_FORMS below
 *   4. Add the option to CERTIFICATE_OPTIONS
 *
 * The General section is shared — only the form below it changes.
 */

const ACORD_FORMS: Record<string, React.ComponentType<any>> = {
  acord25: Acord25Form,
  acord25_101: Acord25With101Form,
  acord25_v2016: Acord25Form,
  acord25_v2016_101: Acord25With101Form,
  acord24: Acord24Form,
  acord24_101: Acord24With101Form,
  acord24_v2016: Acord24Form,
  acord27: Acord27Form,
  acord27_v2016: Acord27Form,
  acord27_101: Acord27With101Form,
  acord28: Acord28Form,
  acord28_v2016: Acord28Form,
  acord28_101: Acord28With101Form,
  acord30: Acord30Form,
  acord22: Acord22Form,
  acord20: Acord20Form,
  acord21: Acord21Form,
  acord23: Acord23Form,
  acord29: Acord29Form,
  acord75: Acord75Form,
  csio0910ecl: Csio0910eclForm,
};

const CERTIFICATE_OPTIONS = [
  // ACORD 25 — only v2016 variants shown; legacy versions hidden below
  // { label: 'CERTIFICATE OF LIABILITY INSURANCE (ACORD 25)', value: 'acord25' },
  // { label: 'CERTIFICATE OF LIABILITY INSURANCE (ACORD 25 + 101)', value: 'acord25_101' },
  { label: 'CERTIFICATE OF LIABILITY INSURANCE (ACORD 25)', value: 'acord25_v2016' },
  // { label: 'CERTIFICATE OF LIABILITY INSURANCE (ACORD 25 v2016 + 101)', value: 'acord25_v2016_101' }, // toggled via ACORD 101 button

  // ACORD 24 — only v2016 variant shown; legacy hidden below
  // { label: 'CERTIFICATE OF PROPERTY INSURANCE (ACORD 24)', value: 'acord24' },
  // { label: 'CERTIFICATE OF PROPERTY INSURANCE (ACORD 24 + 101)', value: 'acord24_101' }, // toggled via ACORD 101 button
  { label: 'CERTIFICATE OF PROPERTY INSURANCE (ACORD 24)', value: 'acord24_v2016' },

  // { label: 'EVIDENCE OF PROPERTY INSURANCE (ACORD 27)', value: 'acord27' }, // legacy 2009/12
  { label: 'EVIDENCE OF PROPERTY INSURANCE (ACORD 27)', value: 'acord27_v2016' },

  // ACORD 28 — only v2016 variant shown; legacy version hidden below
  // { label: 'EVIDENCE OF COMMERCIAL PROPERTY INSURANCE  (ACORD 28)', value: 'acord28' },
  { label: 'EVIDENCE OF COMMERCIAL PROPERTY INSURANCE (ACORD 28)', value: 'acord28_v2016' },

  // { label: 'CERTIFICATE OF GARAGE INSURANCE  (ACORD 30)', value: 'acord30' },
  // { label: 'INTERMODAL INTERCHANGE CERTIFICATE OF INSURANCE (ACORD 22)', value: 'acord22' },
  // { label: 'CERTIFICATE OF AVIATION LIABILITY INSURANCE (ACORD 20)', value: 'acord20' },
  // { label: 'CERTIFICATE OF AIRCRAFT INSURANCE (ACORD 21)', value: 'acord21' },
  // { label: 'VEHICLE OR EQUIPMENT CERTIFICATE OF INSURANCE  (ACORD 23)', value: 'acord23' },
  //{ label: 'EVIDENCE OF FLOOD INSURANCE  (ACORD 29)', value: 'acord29' },
  // { label: 'INSURANCE BINDER (ACORD 75)', value: 'acord75' },
  // { label: 'CSIO0910ECL', value: 'csio0910ecl' },
];

/** Forms whose Locations picker depends on a policy being selected (tracker #241 hint wording). */
const LOCATION_FORM_KEYS = ['acord24', 'acord24_101', 'acord24_v2016', 'acord27', 'acord27_v2016', 'acord27_101', 'acord28', 'acord28_v2016', 'acord28_101', 'acord29'];

/** Certs that have an ACORD 101 endorsement variant. Key = base, Value = +101 key. */
const BASE_TO_101: Record<string, string> = {
  acord25_v2016: 'acord25_v2016_101',
  acord24:       'acord24_101',
  acord24_v2016: 'acord24_101',
  acord27_v2016: 'acord27_101',
  acord28_v2016: 'acord28_101',
};
/** Reverse of BASE_TO_101 — lets us recover the base cert from a +101 key. */
const CERT_101_TO_BASE: Record<string, string> = {
  acord25_v2016_101: 'acord25_v2016',
  acord24_101:       'acord24_v2016',
  acord27_101:       'acord27_v2016',
  acord28_101:       'acord28_v2016',
};

const FORM_NUMBER_TO_KEY: Record<number, string> = {
  20: 'acord20', 21: 'acord21', 22: 'acord22', 23: 'acord23',
  24: 'acord24', 2416: 'acord24_v2016', 24101: 'acord24_101',
  25: 'acord25', 25101: 'acord25_101',
  2516: 'acord25_v2016', 16101: 'acord25_v2016_101',
  27: 'acord27', 2716: 'acord27_v2016', 27101: 'acord27_101',
  28: 'acord28', 2816: 'acord28_v2016', 28101: 'acord28_101', 29: 'acord29', 30: 'acord30',
  75: 'acord75',
};


function resolveFormKey(cert: MasterCertificate): string {
  const num = cert.acordFormNumber;
  return FORM_NUMBER_TO_KEY[num] ?? 'acord25_v2016';
}

// function bitmaskToOptions(bitmask: number | undefined): string[] {
//   if (!bitmask) return [];
//   return Object.entries(BLANKET_VALUE_MAP)
//     .filter(([, val]) => (bitmask & val) === val)
//     .map(([key]) => key);
// }

const BLANKET_OPTIONS = [
  { label: 'General Liability', value: 'general_liability' },
  { label: 'Auto Liability', value: 'auto_liability' },
  { label: 'Umbrella Liability', value: 'umbrella_liability' },
  { label: 'Other Liability 1', value: 'other_liability_1' },
  { label: 'Other Liability 2', value: 'other_liability_2' },
  { label: 'Workers Compensation', value: 'workers_compensation' },
];

export default function AddMastercertificate({ editCert }: { editCert?: MasterCertificate | null }) {
  const routerNavigate = useNavigate();
  const navigateHome = React.useCallback(() => {
    window.location.href = '/agentdashboard';
  }, []);
  const toastRef = React.useRef<Toast>(null);
  const acord25Ref = React.useRef<Acord25FormRef>(null);
  // Policies picker, so the "Select a policy" dialog button can bring the agent back to it.
  const POLICIES_PICKER_ID = 'masterCertPoliciesPicker';
  const policiesPickerRef = React.useRef<MultiSelect>(null);
  const focusPolicyPicker = () => {
    setNoPolicyDialog(null);
    // Wait for the modal to unmount first: PrimeReact's Dialog restores the body scroll
    // position on hide, which would undo a scroll started before that.
    window.setTimeout(() => {
      // Instant, not smooth: smooth scrollIntoView is ignored inside the page's scroll container.
      const el = document.getElementById(POLICIES_PICKER_ID) ?? policiesPickerRef.current?.getElement();
      el?.scrollIntoView({ block: 'center' });
      window.setTimeout(() => policiesPickerRef.current?.show(), 150);
    }, 250);
  };
  const acord24Ref = React.useRef<Acord24FormRef>(null);
  const acord27Ref = React.useRef<Acord27FormRef>(null);
  const acord28Ref = React.useRef<Acord28FormRef>(null);
  const { insuredId, client } = useSelectedClient();
  const [selectedCertificate, setSelectedCertificate] = React.useState<string | null>(
    editCert ? resolveFormKey(editCert) : 'acord25_v2016'
  );
  const [policyOptions, setPolicyOptions] = React.useState<{ label: string; value: string; effectiveDate: string | null; expirationDate: string | null; carrierName: string; linesOfBusiness: string[]; additionalNames: string; policyType: string | null; number: string }[]>([]);
  // policyOptions is already filtered to active (non-cancelled) policies at fetch time — used to
  // keep additional-interest matching from counting cancelled policies still present in selectedPolicies
  // (edit mode intentionally keeps cancelled policy IDs in selectedPolicies so coverage prefill still fires).
  const activePolicyIds = React.useMemo(() => policyOptions.map(o => o.value), [policyOptions]);
  const [selectedPolicies, setSelectedPolicies] = React.useState<string[]>([]);
  const [policiesLoading, setPoliciesLoading] = React.useState(false);
  const [policiesRefreshKey, setPoliciesRefreshKey] = React.useState(0);
  const [showAddPolicy, setShowAddPolicy] = React.useState(false);
  const pendingNewPolicyRef = React.useRef<{ databaseId: string | null; policyNumber: string } | null>(null);
  const [selectedBlanketAI, setSelectedBlanketAI] = React.useState<string[]>([]);
  const [selectedBlanketWOS, setSelectedBlanketWOS] = React.useState<string[]>([]);
  const useInHOA = editCert?.useInHoaunit ?? false;
  const [creating, setCreating] = React.useState(false);
  const [coveragesLoading, setCoveragesLoading] = React.useState(false);
  // Policy numbers the ACORD 25 form found in its coverage rows after the last prefill. See unmappedPolicies.
  const [mappedPolicyNumbers, setMappedPolicyNumbers] = React.useState<string[]>([]);
  const [buildingCoveragesLoading, setBuildingCoveragesLoading] = React.useState(false);
  const [archiveDialog, setArchiveDialog] = React.useState(false);
  // Tracker #241: a policy is never required (Farmers, 9/24). Every state here is a warning with
  // a way back to the picker and a "…anyway" action: 'save'/'preview' for no policy selected,
  // 'unmapped'/'unmappedPreview' for a selected policy that filled no coverage row.
  const [noPolicyDialog, setNoPolicyDialog] = React.useState<'save' | 'preview' | 'unmapped' | 'unmappedPreview' | null>(null);
  const [archiving, setArchiving] = React.useState(false);
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);

  // Preview renders the form as it stands right now, saved or not. The payload is the same one
  // Save sends; the API forwards it to NowCerts PreviewCertificateDraft, which renders a transient
  // draft and persists nothing. (The dashboard card's preview still shows the saved certificate.)
  const handlePreview = () => {
    if (!insuredId || !selectedCertificate) return;
    if (noPolicySelected) {
      setNoPolicyDialog('preview');
      return;
    }
    if (unmappedPolicies.length > 0) {
      setNoPolicyDialog('unmappedPreview');
      return;
    }
    void performPreview();
  };

  const performPreview = async () => {
    if (!insuredId) return;
    const payload = await buildPayload({ createCarriers: false });
    if (!payload) return;
    setPreviewUrl(null);
    setPreviewVisible(true);
    setIsLoadingPreview(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/preview-master-certificate-draft`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ insuredDatabaseId: insuredId, certificateId: editCert?.databaseId ?? '', ...payload }),
      });
      // Three different failures used to share one toast. Name them, so support can tell a
      // route that isn't deployed from a render error from a render that couldn't be stored.
      const fail = (detail: string) => {
        setPreviewVisible(false);
        toastRef.current?.show({ severity: 'error', summary: 'Preview Failed', detail, life: 6000 });
      };
      if (response.status === 404) {
        // The draft route (render the unsaved form) isn't deployed on this API yet. Preview itself
        // always existed for a saved certificate, so in edit mode fall back to that route and say
        // what the PDF is; in create mode there is nothing saved to show.
        if (!editCert) { fail('Previewing an unsaved certificate is not deployed on this environment yet. Create it, then preview from the dashboard.'); return; }
        const saved = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/preview-certificate`, {
          method: 'POST',
          headers: { 'NCToken': ncToken, 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify({ insuredDatabaseId: insuredId, certificateId: editCert.databaseId }),
        });
        const savedData = await saved.json().catch(() => null);
        if (!saved.ok || !savedData?.success || !savedData.previewUrl) { fail('The saved certificate could not be rendered.'); return; }
        setPreviewUrl(savedData.previewUrl);
        toastRef.current?.show({ severity: 'info', summary: 'Showing the saved certificate', detail: 'Unsaved changes are not in this preview: live preview is not deployed on this environment yet.', life: 8000 });
        return;
      }
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        fail(data?.message ? `${data.message}.` : 'The certificate could not be rendered.');
        return;
      }
      if (!data.previewUrl) { fail('The certificate rendered but could not be stored for preview.'); return; }
      setPreviewUrl(data.previewUrl);
    } catch {
      setPreviewVisible(false);
      toastRef.current?.show({ severity: 'error', summary: 'Preview Failed', detail: 'Could not reach the preview service.', life: 6000 });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmArchive = async () => {
    if (!editCert) return;
    setArchiving(true);
    try {
      await archiveMasterCertificate(editCert.databaseId, insuredId ?? '', false, editCert.name);
      toastRef.current?.show({ severity: 'success', summary: 'Archived', detail: `"${editCert.name}" has been archived.`, life: 2000 });
      setTimeout(navigateHome, 2000);
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to archive certificate. Please try again.', life: 4000 });
    } finally {
      setArchiving(false);
      setArchiveDialog(false);
    }
  };

  // Derive 101 toggle state from selectedCertificate — no separate state needed
  const baseCert = CERT_101_TO_BASE[selectedCertificate ?? ''] ?? selectedCertificate ?? '';
  const has101Option = baseCert in BASE_TO_101;
  const is101Active  = (selectedCertificate ?? '') in CERT_101_TO_BASE;

  const handle101Toggle = (checked: boolean) => {
    setSelectedCertificate(checked ? BASE_TO_101[baseCert] : baseCert);
  };

  // Combined "AdditionalNames" custom field across all currently selected policies —
  // surfaced as "Additional Named Insured(s): ..." in the ACORD 101 remarks textarea.
  const selectedAdditionalNamedInsureds = React.useMemo(() => {
    // Each policy's AdditionalNames is a pre-formatted string (may itself contain commas,
    // e.g. "SOUTH BAY BREAD COMPANY, LLC") — dedupe whole values, don't split on comma.
    const values = selectedPolicies
      .map(id => policyOptions.find(o => o.value === id)?.additionalNames)
      .filter((v): v is string => !!v);
    return Array.from(new Set(values)).join(', ');
  }, [selectedPolicies, policyOptions]);

  // Automobile Trades (ASR) policies carry their own garage-liability coverage, so the
  // standalone CGL section needs special handling — see isAutomobileTradesPolicy on Acord25Form.
  const selectedIsAutomobileTrades = React.useMemo(() => {
    return selectedPolicies.some(id => policyOptions.find(o => o.value === id)?.policyType === 'Automobile Trades');
  }, [selectedPolicies, policyOptions]);

  const [allProperties, setAllProperties] = React.useState<{ label: string; value: string; policyIds: string[] }[]>([]);
  const [selectedProperties, setSelectedProperties] = React.useState<string[]>([]);
  const propertyOptions = React.useMemo(() => allProperties, [allProperties]);

  // Clear stale property selections when filtered options change
  React.useEffect(() => {
    setSelectedProperties(prev =>
      prev.filter(id => propertyOptions.some(p => p.value === id))
    );
  }, [propertyOptions]);

  const [propertiesLoading, setPropertiesLoading] = React.useState(false);
  const [vehicleOptions, setVehicleOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [selectedVehicles, setSelectedVehicles] = React.useState<string[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = React.useState(false);

  React.useEffect(() => {
    if (!insuredId) {
      setPolicyOptions([]);
      return;
    }
    let cancelled = false;
    const fetchPolicies = async () => {
      setPoliciesLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-policies`,
          { insuredId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
              'Content-Type': 'application/json',
              NCToken: localStorage.getItem('NCToken') || '',
            },
          }
        );
        if (cancelled) return;
        const raw: any[] = Array.isArray(res.data) ? res.data : [];
        const options = raw
          .filter((p: any) => p.active === true && !p.isQuote)
          .map((p: any) => {
            const name = p.number || p.policyNumber || 'Policy';
            const parsedCustomFields = (() => {
              if (!p.customFields || typeof p.customFields !== 'string') return null;
              try {
                const parsed = JSON.parse(p.customFields);
                return Array.isArray(parsed) ? parsed : null;
              } catch { return null; }
            })();
            const customFieldsLobDisplay = parsedCustomFields?.find((item: any) => item?.Key === 'PolicyType')?.Value
              ? String(parsedCustomFields.find((item: any) => item?.Key === 'PolicyType').Value)
              : null;
            const additionalNames = parsedCustomFields?.find((item: any) => item?.Key === 'AdditionalNames')?.Value
              ? String(parsedCustomFields.find((item: any) => item?.Key === 'AdditionalNames').Value)
              : '';
            const lob = customFieldsLobDisplay
              ? ` (${customFieldsLobDisplay})`
              : Array.isArray(p.linesOfBusiness) && p.linesOfBusiness.length
                ? ` (${p.linesOfBusiness.join(', ').replace(/\bWorker's Compensation\b/g, "Workers' Compensation")})` : '';
            return {
              label: `${name}${lob}`,
              number: name,
              value: p.databaseId,
              effectiveDate: p.effectiveDate || null,
              expirationDate: p.expirationDate || null,
              carrierName: p.carrierName || '',
              linesOfBusiness: Array.isArray(p.linesOfBusiness) ? p.linesOfBusiness : [],
              additionalNames,
              policyType: customFieldsLobDisplay,
            };
          })
          .sort((a, b) => {
            const aTime = a.effectiveDate ? new Date(a.effectiveDate).getTime() : NaN;
            const bTime = b.effectiveDate ? new Date(b.effectiveDate).getTime() : NaN;
            const aUndated = Number.isNaN(aTime);
            const bUndated = Number.isNaN(bTime);
            if (aUndated && bUndated) return 0;
            if (aUndated) return 1;
            if (bUndated) return -1;
            return bTime - aTime;
          });
        setPolicyOptions(options);
      } catch (err: any) {
        if (err?.response?.status === 401) { window.localStorage.clear(); routerNavigate('/'); return; }
        if (!cancelled) setPolicyOptions([]);
      } finally {
        if (!cancelled) setPoliciesLoading(false);
      }
    };
    fetchPolicies();
    return () => { cancelled = true; setPoliciesLoading(false); };
  }, [insuredId, routerNavigate, policiesRefreshKey]);

  const editPolicyIdsRef = React.useRef<string[] | null>(
    editCert ? editCert.policies.map(p => p.databaseId) : null
  );
  React.useEffect(() => {
    if (!editPolicyIdsRef.current || policyOptions.length === 0) return;
    // Use all policy IDs from the edit cert directly — don't filter by policyOptions
    // so that coverage prefill fires even for policies no longer active in the list.
    // Skip the set entirely for a cert with no linked policies: a fresh [] reference would
    // re-run the forms' coverage effect and clear the stored XML they restored on mount.
    if (editPolicyIdsRef.current.length > 0) setSelectedPolicies(editPolicyIdsRef.current);
    editPolicyIdsRef.current = null;
  }, [policyOptions]);

  // Tracker #257 (Farmers, 10/9; JD 10/14): no one-policy-per-line rule. The POLICY NUMBER cells
  // are typed, so a second policy on the same line simply shows as "didn't populate" until the
  // agent types its number into the row they want. Autopopulate is unchanged: the AMS still
  // fills each row from the first policy it classifies for it.
  const selectedPoliciesRef = React.useRef<string[]>(selectedPolicies);
  React.useEffect(() => { selectedPoliciesRef.current = selectedPolicies; }, [selectedPolicies]);

  React.useEffect(() => {
    const pending = pendingNewPolicyRef.current;
    if (!pending || policyOptions.length === 0) return;
    const match = policyOptions.find(o =>
      (pending.databaseId && o.value === pending.databaseId) ||
      o.label.startsWith(pending.policyNumber)
    );
    pendingNewPolicyRef.current = null;
    if (match) {
      const current = selectedPoliciesRef.current;
      setSelectedPolicies(current.includes(match.value) ? current : [...current, match.value]);
    } else {
      toastRef.current?.show({
        severity: 'info',
        summary: 'Policy Created',
        detail: 'Policy created — it may take a moment to appear in the list.',
        life: 5000,
      });
    }
  }, [policyOptions]);

  React.useEffect(() => {
    if (!insuredId || !selectedPolicies.length || !['acord24', 'acord24_101', 'acord24_v2016', 'acord27', 'acord27_v2016', 'acord27_101', 'acord28', 'acord28_v2016', 'acord28_101', 'acord29'].includes(selectedCertificate || '')) {
      setAllProperties([]);
      setPropertiesLoading(false);
      return;
    }
    let cancelled = false;
    const fetchProperties = async () => {
      setPropertiesLoading(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const authToken = localStorage.getItem('igtoken') || '';
        const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=100&insuredDatabaseId=${encodeURIComponent(insuredId)}&policyIds=${encodeURIComponent(selectedPolicies.join(','))}`;
        const res = await fetch(url, {
          headers: {
            'NCToken': ncToken,
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        });
        if (cancelled) return;
        const data = await res.json();
        if (Array.isArray(data.value)) {
          const options = data.value.map((property: any) => ({
            label: property.addressLine1
              ? `${property.addressLine1}${property.addressLine2 ? `, ${property.addressLine2}` : ''}${property.city ? `, ${property.city}` : ''}${property.state ? `, ${property.state}` : ''}${property.zip ? ` ${property.zip}` : ''}`
              : (property.description || property.propertyUse || 'Property'),
            value: property.databaseId,
            policyIds: Array.isArray(property.policyIds) ? property.policyIds : [],
          }));
          if (!cancelled) setAllProperties(options);
        } else {
          if (!cancelled) setAllProperties([]);
        }
      } catch (err: any) {
        if (err?.response?.status === 401) { window.localStorage.clear(); routerNavigate('/'); return; }
        if (!cancelled) setAllProperties([]);
      } finally {
        if (!cancelled) setPropertiesLoading(false);
      }
    };
    fetchProperties();
    return () => { cancelled = true; setPropertiesLoading(false); };
  }, [insuredId, selectedCertificate, selectedPolicies, routerNavigate]);

  React.useEffect(() => {
    if (!insuredId || selectedCertificate !== 'acord23') {
      setVehicleOptions([]);
      setVehiclesLoading(false);
      return;
    }
    let cancelled = false;
    const fetchVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const authToken = localStorage.getItem('igtoken') || '';
        const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-vehicles?insuredDatabaseId=${encodeURIComponent(insuredId)}`;
        const res = await fetch(url, {
          headers: {
            'NCToken': ncToken,
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        });
        if (cancelled) return;
        const data = await res.json();
        if (Array.isArray(data.value)) {
          const options = data.value.map((vehicle: any) => ({
            label: vehicle.vin || vehicle.typeDescription || 'Vehicle',
            value: vehicle.databaseId
          }));
          if (!cancelled) setVehicleOptions(options);
        } else {
          if (!cancelled) setVehicleOptions([]);
        }
      } catch (err: any) {
        if (err?.response?.status === 401) { window.localStorage.clear(); routerNavigate('/'); return; }
        if (!cancelled) setVehicleOptions([]);
      } finally {
        if (!cancelled) setVehiclesLoading(false);
      }
    };
    fetchVehicles();
    return () => { cancelled = true; setVehiclesLoading(false); };
  }, [insuredId, selectedCertificate, routerNavigate]);

  const noPolicySelected = !policiesLoading && selectedPolicies.length === 0;
  // A selected policy NowCerts did not classify for this form fills no coverage row (a Life-Health
  // "Umbrella Coverage" line, for example). Nothing tells the agent, the cells stay blank,
  // and Save would store a blank certificate with a linked policy — past every no-policy guard.
  // Only the ACORD 25 forms report their mapping; the property forms read generic slots that always fill.
  const unmappedPolicies = React.useMemo(
    () => (coveragesLoading || !(selectedCertificate || '').startsWith('acord25'))
      ? []
      : findUnmappedPolicies(selectedPolicies, policyOptions, mappedPolicyNumbers),
    [coveragesLoading, selectedCertificate, selectedPolicies, policyOptions, mappedPolicyNumbers]
  );
  const formUsesLocations = LOCATION_FORM_KEYS.includes(selectedCertificate || '');

  // Tracker #241: agents were creating master certs with no policy and getting a blank
  // certificate. Decision 9/24 (Farmers): warn, never block — some certificates are
  // legitimately built without a policy in the AMS. The dialogs explain what will be blank
  // and offer the picker; the agent can still create/save/preview anyway.
  const handleCreate = () => {
    if (!selectedCertificate || !insuredId) return;
    if (noPolicySelected) {
      setNoPolicyDialog('save');
      return;
    }
    if (unmappedPolicies.length > 0) {
      setNoPolicyDialog('unmapped');
      return;
    }
    void performCreate();
  };

  // Tracker #257: typed carriers become AMS records at Save time (see utils/carrierSlots.ts).
  // Preview passes createCarriers=false so looking at a draft never writes a carrier; a carrier
  // the agent typed but has not saved yet prints without an NAIC on the preview only.
  const carrierDeps: CarrierSlotDeps = React.useMemo(() => ({
    lookupById: resolveCarrierById,
    insert: async (name, naic) => {
      try {
        return await insertCarrier(name, naic);
      } catch (err: any) {
        throw new Error(`Could not create carrier "${name}" in the AMS. ${err?.message || ''}`.trim());
      }
    },
  }), []);
  const resolveCompanySlot = async (slot: { name: string; id: string | null; naic: string }): Promise<string | null> =>
    (await ensureCarrierIds([slot], carrierDeps))[0];

  // Assembles the create/edit payload from the live form state. Shared by Save and Preview so
  // the preview PDF is rendered from exactly what Save would send.
  const buildPayload = async ({ createCarriers }: { createCarriers: boolean }) => {
    if (!selectedCertificate || !insuredId) return null;

    const formNumber = ACORD_FORM_NUMBERS[selectedCertificate];
    const isAcord25 = selectedCertificate === 'acord25' || selectedCertificate === 'acord25_101' || selectedCertificate === 'acord25_v2016' || selectedCertificate === 'acord25_v2016_101';
    const isAcord24 = selectedCertificate === 'acord24' || selectedCertificate === 'acord24_101' || selectedCertificate === 'acord24_v2016';
    const isAcord27 = selectedCertificate === 'acord27' || selectedCertificate === 'acord27_v2016' || selectedCertificate === 'acord27_101';
    const isAcord28 = selectedCertificate === 'acord28' || selectedCertificate === 'acord28_v2016' || selectedCertificate === 'acord28_101';

    let coverages: CoverageEntry[] = [];
    const blanketAI: number[] = [];
    const blanketWOS: number[] = [];
    if (isAcord25 && acord25Ref.current) {
      const acord25Data = acord25Ref.current.getFormData();
      if (createCarriers) await applyInsurerSlots(acord25Data, carrierDeps);
      coverages = buildAcord25Coverages(acord25Data, formNumber);
      if (acord25Data.cglAddlInsd) blanketAI.push(1);
      if (acord25Data.autoAddlInsd) blanketAI.push(2);
      if (acord25Data.umbrellaAddlInsd) blanketAI.push(4);
      if (acord25Data.other1AddlInsd) blanketAI.push(8);
      if (acord25Data.wcAddlInsd) blanketAI.push(32);
      if (acord25Data.cglSbrWvd) blanketWOS.push(1);
      if (acord25Data.autoSbrWvd) blanketWOS.push(2);
      if (acord25Data.umbrellaSbrWvd) blanketWOS.push(4);
      if (acord25Data.other1SbrWvd) blanketWOS.push(8);
      if (acord25Data.wcSbrWvd) blanketWOS.push(32);
    } else if (isAcord24 && acord24Ref.current) {
      const acord24Data = acord24Ref.current.getFormData();
      if (createCarriers) await applyInsurerSlots(acord24Data, carrierDeps);
      coverages = buildAcord24Coverages(acord24Data, formNumber);
    } else if (isAcord27 && acord27Ref.current) {
      const acord27Data = acord27Ref.current.getFormData();
      if (createCarriers) acord27Data.companyId = await resolveCompanySlot({ name: acord27Data.company, id: acord27Data.companyId, naic: acord27Data.companyNaic });
      coverages = buildAcord27Coverages(acord27Data, formNumber);
    } else if (isAcord28 && acord28Ref.current) {
      const acord28Data = acord28Ref.current.getFormData();
      if (createCarriers) acord28Data.companyNaicId = await resolveCompanySlot({ name: acord28Data.companyName, id: acord28Data.companyNaicId, naic: acord28Data.naicNo });
      coverages = buildAcord28Coverages(acord28Data, formNumber);
    }

    return {
      TruckingCompanyId: insuredId,
      AcordFormNumber: formNumber,
      Name: certDisplayName,
      UseInHoaUnit: useInHOA,
      PolicyIds: selectedPolicies,
      BuildingIds: selectedProperties,
      Coverages: coverages,
      BlanketAdditionalInterestTypes: blanketAI,
      BlanketWaiverofSubrogation: blanketWOS,
    };
  };

  const performCreate = async () => {
    try {
      setCreating(true);
      // Creates any typed carriers first; a failure there surfaces like a failed save.
      const basePayload = await buildPayload({ createCarriers: true });
      if (!basePayload) return;
      if (editCert) {
        await editMasterCertificate({ ...basePayload, certificateId: editCert.databaseId });
      } else {
        await createMasterCertificate(basePayload);
      }
      toastRef.current?.show({ severity: 'success', summary: 'Success', detail: editCert ? 'Certificate updated successfully' : 'Certificate created successfully', life: 2000 });
      setTimeout(navigateHome, 2000);
    } catch (err: any) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || (editCert ? 'Failed to update certificate' : 'Failed to create certificate'), life: 4000 });
    } finally {
      setCreating(false);
    }
  };

  const AcordFormComponent = selectedCertificate ? ACORD_FORMS[selectedCertificate] : null;

  const baseOption = CERTIFICATE_OPTIONS.find(o => o.value === baseCert);
  const certLabel = baseOption
    ? (is101Active ? `${baseOption.label} + ACORD 101` : baseOption.label)
    : '';
  const defaultCertName = certLabel
    ? `${certLabel} (${new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})`
    : '';
  const [certDisplayName, setCertDisplayName] = React.useState(editCert?.name || defaultCertName);

  React.useEffect(() => {
    if (editCert) return; // keep existing name when editing
    if (!certLabel) return;
    const generated = `${certLabel} (${new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})`;
    setCertDisplayName(generated);
  }, [selectedCertificate]);

  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (editCert) return;
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setSelectedPolicies([]);
    setSelectedProperties([]);
    setSelectedVehicles([]);
    setSelectedBlanketAI([]);
    setSelectedBlanketWOS([]);
  }, [selectedCertificate]);

  const renderFooter = () => (
    <div className="masterCert__footer" style={{ justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          label="Preview"
          icon="pi pi-eye"
          className="p-button-text"
          style={{ color: '#6d28d9' }}
          onClick={handlePreview}
          disabled={creating || isLoadingPreview || coveragesLoading || propertiesLoading || policiesLoading || vehiclesLoading || buildingCoveragesLoading}
        />
        {editCert ? (
          <Button
            label="Archive"
            icon="pi pi-inbox"
            className="p-button-text p-button-danger"
            onClick={() => setArchiveDialog(true)}
            disabled={creating}
          />
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          label="Close"
          className="appButton--secondary"
          onClick={navigateHome}
        />
        <Button
          label={creating ? (editCert ? 'Saving...' : 'Creating...') : (coveragesLoading || propertiesLoading || policiesLoading || vehiclesLoading || buildingCoveragesLoading) ? 'Loading...' : (editCert ? 'Save changes' : 'Create master certificate')}
          className="masterCert__btnPrimary"
          onClick={handleCreate}
          disabled={creating || coveragesLoading || propertiesLoading || policiesLoading || vehiclesLoading || buildingCoveragesLoading}
          loading={creating || coveragesLoading || propertiesLoading || policiesLoading || vehiclesLoading || buildingCoveragesLoading}
        />
      </div>
    </div>
  );

  return (
    <div className="sendCertPage__container">
      <Toast ref={toastRef} position="top-center" />
      <PreviewSendCertificate
        visible={previewVisible}
        onHide={() => setPreviewVisible(false)}
        pdfBlobUrl={previewUrl}
        isLoadingPdf={isLoadingPreview}
      />
      <Dialog
        visible={archiveDialog}
        onHide={() => { if (!archiving) setArchiveDialog(false); }}
        header={null}
        footer={null}
        closable={!archiving}
        className="archiveConfirmDialog"
        style={{ width: 440 }}
        modal
        draggable={false}
        resizable={false}
      >
        <div className="archiveDialog__iconWrap">
          <i className="pi pi-inbox archiveDialog__icon" />
        </div>
        <div className="archiveDialog__title">Archive Master Certificate</div>
        <div className="archiveDialog__message">
          Are you sure you want to archive <strong>"{editCert?.name}"</strong>?
          <br />It will be removed from the active list and can no longer be used to send certificates.
        </div>
        <div className="archiveDialog__footer">
          <Button
            label="Cancel"
            className="p-button-text archiveDialog__cancelBtn"
            onClick={() => setArchiveDialog(false)}
            disabled={archiving}
          />
          <Button
            label={archiving ? 'Archiving...' : 'Archive'}
            className="archiveDialog__archiveBtn"
            onClick={handleConfirmArchive}
            loading={archiving}
            icon="pi pi-inbox"
          />
        </div>
      </Dialog>
      <Dialog
        visible={noPolicyDialog !== null}
        onHide={() => { if (!creating) setNoPolicyDialog(null); }}
        header={null}
        footer={null}
        closable={!creating}
        className="archiveConfirmDialog noPolicyDialog"
        style={{ width: 440 }}
        modal
        draggable={false}
        resizable={false}
      >
        <div className="archiveDialog__iconWrap noPolicyDialog__iconWrap">
          <i className="pi pi-exclamation-triangle archiveDialog__icon noPolicyDialog__icon" />
        </div>
        {noPolicyDialog === 'unmapped' || noPolicyDialog === 'unmappedPreview' ? (
          <>
            <div className="archiveDialog__title">{unmappedPolicies.length === 1 ? 'A selected policy didn\'t populate' : 'Selected policies didn\'t populate'}</div>
            <div className="archiveDialog__message">
              <ul className="noPolicyDialog__list">
                {unmappedPolicies.map(o => <li key={o.value}><strong>{o.number || '(no number)'}</strong> ({describePolicyLob(o)})</li>)}
              </ul>
              NowCerts returned no coverage data for {unmappedPolicies.length === 1 ? 'this policy' : 'these policies'}, so no policy number, dates or limits can fill from {unmappedPolicies.length === 1 ? 'it' : 'them'}.
              {' '}Check the Line of Business in NowCerts — the certificate needs a Commercial or Personal line such as General Liability, Commercial Auto, Commercial Umbrella or Worker's Compensation — or deselect {unmappedPolicies.length === 1 ? 'it' : 'them'}.
              {noPolicyDialog === 'unmappedPreview'
                ? <> The preview will show {unmappedPolicies.length === 1 ? 'that row' : 'those rows'} blank.</>
                : <> If you submit anyway, {unmappedPolicies.length === 1 ? 'that row' : 'those rows'} will be blank on the certificate.</>}
            </div>
            <div className="archiveDialog__footer">
              {noPolicyDialog === 'unmappedPreview' ? (
                <>
                  <Button label="Edit Certificate" className="p-button-text archiveDialog__cancelBtn" onClick={focusPolicyPicker} autoFocus />
                  <Button label="Preview" className="masterCert__btnPrimary noPolicyDialog__confirmBtn" onClick={() => { setNoPolicyDialog(null); void performPreview(); }} />
                </>
              ) : (
                <>
                  <Button label="Edit Certificate" className="p-button-text archiveDialog__cancelBtn" onClick={focusPolicyPicker} disabled={creating} autoFocus />
                  <Button
                    label={creating ? 'Submitting...' : 'Submit'}
                    className="masterCert__btnPrimary noPolicyDialog__confirmBtn"
                    onClick={async () => { await performCreate(); setNoPolicyDialog(null); }}
                    loading={creating}
                  />
                </>
              )}
            </div>
          </>
        ) : noPolicyDialog === 'preview' ? (
          <>
            <div className="archiveDialog__title">Are you sure you want to preview this certificate without selecting a policy number?</div>
            <div className="archiveDialog__message">
              If you don't select a policy number from the drop down, the preview will not include pre-filled policy numbers, effective dates, carriers or coverages{formUsesLocations ? ', or a location address' : ''}.
            </div>
            <div className="archiveDialog__footer">
              <Button
                label="Edit Certificate"
                className="p-button-text archiveDialog__cancelBtn"
                onClick={focusPolicyPicker}
                autoFocus
              />
              <Button
                label="Preview"
                className="masterCert__btnPrimary noPolicyDialog__confirmBtn"
                onClick={() => { setNoPolicyDialog(null); void performPreview(); }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="archiveDialog__title">Are you sure you want to {editCert ? 'save' : 'create'} this certificate without selecting a policy number?</div>
            <div className="archiveDialog__message">
              If you don't select a policy number from the drop down, the certificate will not include pre-filled policy numbers, effective dates, carriers or coverages{formUsesLocations ? ', or a location address' : ''}.
            </div>
            <div className="archiveDialog__footer">
              <Button
                label="Edit Certificate"
                className="p-button-text archiveDialog__cancelBtn"
                onClick={focusPolicyPicker}
                disabled={creating}
                autoFocus
              />
              <Button
                label={creating ? 'Submitting...' : 'Submit'}
                className="masterCert__btnPrimary noPolicyDialog__confirmBtn"
                onClick={async () => { await performCreate(); setNoPolicyDialog(null); }}
                loading={creating}
              />
            </div>
          </>
        )}
      </Dialog>
      <Dialog
        visible={showAddPolicy}
        onHide={() => setShowAddPolicy(false)}
        header={`Add Policy${client?.name ? ` — ${client.name}` : ''}`}
        className="addPolicyDialog"
        style={{ width: '720px' }}
        contentStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        modal
        draggable={false}
        resizable={false}
      >
        <AddPolicyForm
          insuredId={insuredId ?? ''}
          insuredName={client?.name}
          onSuccess={(created) => {
            setShowAddPolicy(false);
            pendingNewPolicyRef.current = created;
            setPoliciesRefreshKey((k) => k + 1);
            if (insuredId) emitPolicyCreated(insuredId);
          }}
          onCancel={() => setShowAddPolicy(false)}
        />
      </Dialog>
      <div className="appDialog__header masterCert__pageHeader">
        <div className="appDialog__title masterCert__pageTitle">{editCert ? 'Edit master certificate' : 'Add master certificate'}</div>
      </div>
      <div className="sendCertPage__scroll">
        <div className="masterCert__section masterCert__section--general">
          <div className="masterCert__sectionTitle">General</div>
          <div className="masterCert__generalGrid">
            {/* Row 1 */}
            {!editCert && (
            <div>
              <div className="masterCert__fieldLabel" style={{ marginBottom: '4px' }}>Certificate</div>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px', marginBottom: '8px' }}>
                <Dropdown
                  value={baseCert}
                  options={CERTIFICATE_OPTIONS}
                  onChange={(e) => setSelectedCertificate(e.value)}
                  placeholder="Select certificate"
                  className="masterCert__input"
                  style={{ flex: 1, minWidth: 0 }}
                  emptyMessage="No available options"
                  valueTemplate={(option) => {
                    if (!option) return <span className="p-placeholder">Select certificate</span>;
                    return <span>{is101Active ? `${option.label} + ACORD 101` : option.label}</span>;
                  }}
                />
                {has101Option && (
                  <ToggleButton
                    checked={is101Active}
                    onChange={(e) => handle101Toggle(e.value)}
                    onLabel="ACORD 101"
                    offLabel="+ ACORD 101"
                    onIcon="pi pi-check"
                    offIcon=""
                    style={{ flexShrink: 0, fontSize: '12px', padding: '0 14px', borderRadius: '6px', fontWeight: 600, whiteSpace: 'nowrap' }}
                  />
                )}
              </div>
            </div>
            )}
            <div>
              <div className="masterCert__fieldLabel">Name</div>
              <InputText value={certDisplayName} onChange={(e) => setCertDisplayName(e.target.value)} className="masterCert__input masterCert__inputFull masterCert__inputMb" />
            </div>
            {/* Row 2 */}
            <div>
              <div style={{ position: 'relative' }}>
                <div className="masterCert__fieldLabel">Policies</div>
                <Button
                  label="Add Policy"
                  icon="pi pi-plus"
                  outlined
                  size="small"
                  className="carrierDialog__secondaryBtn"
                  onClick={() => setShowAddPolicy(true)}
                  style={{ position: 'absolute', right: 0, bottom: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px', whiteSpace: 'nowrap' }}
                />
              </div>
              <MultiSelect
                ref={policiesPickerRef}
                id={POLICIES_PICKER_ID}
                value={selectedPolicies}
                options={policyOptions}
                onChange={(e) => setSelectedPolicies(e.value as string[])}
                placeholder={policiesLoading ? 'Loading...' : 'Please select policies...'}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} Policies Selected"
                disabled={policiesLoading}
                filter
                filterPlaceholder="Search policies..."
                className="masterCert__input masterCert__inputFull masterCert__inputMb"
                itemTemplate={(option: any) => {
                  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';
                  return (
                    <div className="policyItem">
                      <div className="policyItem__info">
                        <div className="policyItem__label">{option.label}</div>
                        {option.carrierName && (
                          <div className="policyItem__carrier">{option.carrierName}</div>
                        )}
                      </div>
                      <div className="policyItem__dates">
                        <div className="policyItem__dateRow">
                          <span className="policyItem__badge policyItem__badge--eff">EFF</span>
                          <span className="policyItem__dateVal">{fmt(option.effectiveDate)}</span>
                        </div>
                        <div className="policyItem__dateRow">
                          <span className="policyItem__badge policyItem__badge--exp">EXP</span>
                          <span className="policyItem__dateVal">{fmt(option.expirationDate)}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              {noPolicySelected && (
                <div className="masterCert__fieldHint" role="note">
                  <i className="pi pi-info-circle masterCert__fieldHintIcon" />
                  {policyOptions.length === 0
                    ? <>This insured has no active policies. Use <strong>Add Policy</strong> to add one, or the certificate will be {editCert ? 'saved' : 'created'} without policy data.</>
                    : <>No policy selected. Policy numbers, effective dates, carriers{formUsesLocations ? ', coverage limits and location' : ' and coverage limits'} will not pre-fill on this certificate.</>}
                </div>
              )}
              {unmappedPolicies.length > 0 && (
                <div className="masterCert__fieldHint masterCert__fieldHint--warn" role="alert">
                  <i className="pi pi-exclamation-triangle masterCert__fieldHintIcon" />
                  <span>
                    {unmappedPolicies.map((o, i) => (
                      <React.Fragment key={o.value}>
                        {i > 0 ? <br /> : null}
                        <strong>{o.number || '(no number)'}</strong> ({describePolicyLob(o)}) didn't populate any coverage row. Type its number into the row you want, check its Line of Business in NowCerts, or deselect it.
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              )}
            </div>
            {/* Blanket Waiver of Subrogation — ACORD 25, 24, 27, 28 (all variants) */}
            {/* {['acord25', 'acord25_v2016', 'acord25_101', 'acord25_v2016_101',
              'acord24', 'acord24_v2016', 'acord24_101',
              'acord27', 'acord27_v2016',
              'acord28', 'acord28_v2016'].includes(selectedCertificate || '') && (
              <div>
                <div className="masterCert__fieldLabel masterCert__fieldLabel--required">Blanket Waiver of Subrogation</div>
                <MultiSelect
                  value={selectedBlanketWOS}
                  options={BLANKET_OPTIONS}
                  onChange={(e) => setSelectedBlanketWOS(e.value)}
                  placeholder="Please select..."
                  maxSelectedLabels={2}
                  selectedItemsLabel="{0} Selected"
                  className="masterCert__input masterCert__inputFull masterCert__inputMb"
                />
              </div>
            )} */}

            {/* Blanket Additional Interest — ACORD 25 only (all variants) */}
            {/* {['acord25', 'acord25_v2016', 'acord25_101', 'acord25_v2016_101'].includes(selectedCertificate || '') && (
              <div>
                <div className="masterCert__fieldLabel masterCert__fieldLabel--required">Blanket Additional Interest</div>
                <MultiSelect
                  value={selectedBlanketAI}
                  options={BLANKET_OPTIONS}
                  onChange={(e) => setSelectedBlanketAI(e.value)}
                  placeholder="Please select..."
                  maxSelectedLabels={2}
                  selectedItemsLabel="{0} Selected"
                  className="masterCert__input masterCert__inputFull masterCert__inputMb"
                />
              </div>
            )}
             */}
            {/* Conditional: Properties - For ACORD 24, 28, 29 (hidden in edit mode) */}
            {!editCert && ['acord24', 'acord24_101', 'acord24_v2016', 'acord27', 'acord27_v2016', 'acord27_101', 'acord28', 'acord28_v2016', 'acord28_101', 'acord29'].includes(selectedCertificate || '') && (
              <div>
                <div className="masterCert__fieldLabel">Locations</div>
                <div style={{ position: 'relative' }}>
                  <MultiSelect
                    value={selectedProperties}
                    options={propertyOptions}
                    panelClassName="masterCert__locationPanel"
                    onChange={(e) => {
                      const prev: string[] = selectedProperties;
                      const next: string[] = e.value;
                      // Enforce single selection: keep only the newly added item
                      const newlyAdded = next.find(id => !prev.includes(id));
                      const enforced = newlyAdded ? [newlyAdded] : [];
                      setSelectedProperties(enforced);
                      const isAcord24v2016 = selectedCertificate === 'acord24_v2016';
                      const isAcord24_101 = selectedCertificate === 'acord24_101';
                      const isAcord27v2016 = selectedCertificate === 'acord27_v2016' || selectedCertificate === 'acord27_101';
                      const isAcord28v2016 = selectedCertificate === 'acord28_v2016' || selectedCertificate === 'acord28_101';
                      if (newlyAdded && (isAcord24v2016 || isAcord24_101 || isAcord27v2016 || isAcord28v2016)) {
                        if (isAcord24v2016 || isAcord24_101) acord24Ref.current?.applyBuildingCoverages([]);
                        if (isAcord27v2016) acord27Ref.current?.applyBuildingCoverages([]);
                        if (isAcord28v2016) acord28Ref.current?.applyBuildingCoverages([]);
                        const acordFormNumber = isAcord24v2016 || isAcord24_101 ? 2416 : isAcord27v2016 ? 2716 : 2816;
                        setBuildingCoveragesLoading(true);
                        fetchBuildingCoverages(newlyAdded, acordFormNumber)
                          .then(results => {
                            const allReturnObjects = results.flatMap((r: any) =>
                              Array.isArray(r.returnObjects) ? r.returnObjects : []
                            );
                            if (allReturnObjects.length > 0) {
                              if (isAcord24v2016 || isAcord24_101) acord24Ref.current?.applyBuildingCoverages(allReturnObjects);
                              if (isAcord27v2016) acord27Ref.current?.applyBuildingCoverages(allReturnObjects);
                              if (isAcord28v2016) acord28Ref.current?.applyBuildingCoverages(allReturnObjects);
                            }
                          })
                          .catch((err) => console.error('building-coverages error:', err))
                          .finally(() => setBuildingCoveragesLoading(false));
                      }
                    }}
                    placeholder={propertiesLoading ? 'Loading...' : 'Please select locations...'}
                    maxSelectedLabels={1}
                    selectedItemsLabel="{0} Locations Selected"
                    showSelectAll={false}
                    disabled={propertiesLoading}
                    className="masterCert__input masterCert__inputFull masterCert__inputMb"
                  />
                  {propertiesLoading && (
                    <i className="pi pi-spin pi-spinner" style={{ position: 'absolute', right: '45px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#0057A4' }} />
                  )}
                </div>
              </div>
            )}
            
            {/* Conditional: Vehicle - Only for ACORD 23 */}
            {selectedCertificate === 'acord23' && (
              <div>
                <div className="masterCert__fieldLabel masterCert__fieldLabel--required">Vehicle</div>
                <div style={{ position: 'relative' }}>
                  <MultiSelect
                    value={selectedVehicles}
                    options={vehicleOptions}
                    onChange={(e) => setSelectedVehicles(e.value)}
                    placeholder={vehiclesLoading ? 'Loading...' : 'Please select vehicle...'}
                    maxSelectedLabels={1}
                    selectedItemsLabel="{0} Vehicles Selected"
                    disabled={vehiclesLoading}
                    className="masterCert__input masterCert__inputFull masterCert__inputMb"
                  />
                  {vehiclesLoading && (
                    <i className="pi pi-spin pi-spinner" style={{ position: 'absolute', right: '45px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#0057A4' }} />
                  )}
                </div>
              </div>
            )}
            {/*             
            <div className="masterCert__checkboxWrapper">
              <Checkbox inputId="useInHOA" checked={useInHOA} onChange={(e) => setUseInHOA(e.checked ?? false)} />
              <label htmlFor="useInHOA" className="masterCert__checkboxLabel">Use in HOA Unit</label>
            </div> */}
          </div>
        </div>

        {AcordFormComponent ? (
          (() => {
            const isAcord25 = selectedCertificate === 'acord25' || selectedCertificate === 'acord25_101' || selectedCertificate === 'acord25_v2016' || selectedCertificate === 'acord25_v2016_101';
            const isAcord24 = selectedCertificate === 'acord24' || selectedCertificate === 'acord24_101' || selectedCertificate === 'acord24_v2016';
            const isAcord27 = selectedCertificate === 'acord27' || selectedCertificate === 'acord27_v2016' || selectedCertificate === 'acord27_101';
            const isAcord28 = selectedCertificate === 'acord28' || selectedCertificate === 'acord28_v2016' || selectedCertificate === 'acord28_101';
            const F = AcordFormComponent as React.ForwardRefExoticComponent<any>;
            if (isAcord25) {
              const isV2016 = selectedCertificate === 'acord25_v2016' || selectedCertificate === 'acord25_v2016_101';
              const acord25FileParentId = isV2016
                ? '0B7D6AE3-E0FD-4608-B4E8-5402AFC8E894'
                : 'E5832063-F5A6-40D2-9F85-450461D9523E';
              return <F ref={acord25Ref} selectedPolicies={selectedPolicies} activePolicyIds={activePolicyIds} acord101AdditionalNamedInsureds={selectedAdditionalNamedInsureds} isAutomobileTradesPolicy={selectedIsAutomobileTrades} insuredId={insuredId} fileParentId={acord25FileParentId} editCoverages={editCert?.coverages} editPolicies={editCert?.policies} onLoadingChange={setCoveragesLoading} onCoverageMapping={setMappedPolicyNumbers}
                initialCglAddlInsd={(editCert?.blanketAdditionalInterestTypes ?? []).includes(1)} initialCglSbrWvd={(editCert?.blanketWaiverofSubrogation ?? []).includes(1)}
                initialAutoAddlInsd={(editCert?.blanketAdditionalInterestTypes ?? []).includes(2)} initialAutoSbrWvd={(editCert?.blanketWaiverofSubrogation ?? []).includes(2)}
                initialUmbrellaAddlInsd={(editCert?.blanketAdditionalInterestTypes ?? []).includes(4)} initialUmbrellaSbrWvd={(editCert?.blanketWaiverofSubrogation ?? []).includes(4)}
                initialOther1AddlInsd={(editCert?.blanketAdditionalInterestTypes ?? []).includes(8)} initialOther1SbrWvd={(editCert?.blanketWaiverofSubrogation ?? []).includes(8)}
                initialWcAddlInsd={(editCert?.blanketAdditionalInterestTypes ?? []).includes(32)} initialWcSbrWvd={(editCert?.blanketWaiverofSubrogation ?? []).includes(32)}
              />;
            }
            if (isAcord24) {
              const selectedLocationLabel = selectedProperties
                .map(id => propertyOptions.find(p => p.value === id)?.label || '')
                .filter(Boolean)
                .join('\n');
              return <F ref={acord24Ref} selectedPolicies={selectedPolicies} activePolicyIds={activePolicyIds} acord101AdditionalNamedInsureds={selectedAdditionalNamedInsureds} insuredId={insuredId} selectedLocationLabel={selectedLocationLabel} editCoverages={editCert?.coverages} editPolicies={editCert?.policies} isV2016={selectedCertificate === 'acord24_v2016'} onLoadingChange={setCoveragesLoading} />;
            }
            if (isAcord27) {
              const selectedLocationLabel = selectedProperties
                .map(id => propertyOptions.find(p => p.value === id)?.label || '')
                .filter(Boolean)
                .join('\n');
              return <F ref={acord27Ref} selectedPolicies={selectedPolicies} acord101AdditionalNamedInsureds={selectedAdditionalNamedInsureds} insuredId={insuredId} selectedLocationLabel={selectedLocationLabel} editCoverages={editCert?.coverages} editPolicies={editCert?.policies} isV2016={selectedCertificate === 'acord27_v2016'} onLoadingChange={setCoveragesLoading} />;
            }
            if (isAcord28) {
              const acord28FileParentId = selectedCertificate === 'acord28_v2016'
                ? 'DED94ACD-373F-4696-8620-72B837840065'
                : 'B33EB550-0716-44F5-B395-0632203AE275';
              return <F ref={acord28Ref} selectedPolicies={selectedPolicies} acord101AdditionalNamedInsureds={selectedAdditionalNamedInsureds} selectedProperties={selectedProperties} insuredId={insuredId} fileParentId={acord28FileParentId} isV2016={selectedCertificate === 'acord28_v2016'} editCoverages={editCert?.coverages} editPolicies={editCert?.policies} onLoadingChange={setCoveragesLoading} />;
            }
            return <AcordFormComponent selectedPolicies={selectedPolicies} insuredId={insuredId} />;
          })()
        ) : selectedCertificate ? (
          <div className="masterCert__section" style={{ textAlign: 'center', padding: '48px 32px', color: '#6b7280' }}>
            <i className="pi pi-file" style={{ fontSize: 32, marginBottom: 12, display: 'block', color: '#cbd5e1' }} />
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {certLabel || 'Selected certificate'} form is comming soon.....
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              sorry for the inconvenience its on the way....
            </div>
          </div>
        ) : null}
      </div>
      {renderFooter()}
    </div>
  );
}
